import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import { useSocket } from "../context/SocketContext";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import gorilaReunionImg from "../imagenes/Gorilas/gorila_reunion.png";

export default function ModalFollowup() {
  const { socket } = useSocket();
  const { user } = useAuth();
  const { t } = useTranslation<"translation">();

  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<any>(null);
  const [option, setOption] = useState<"1w" | "2w" | "3w" | "1m" | "custom" | "none">("1w");
  const [customDate, setCustomDate] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState<string | null>(null);
  const [estadoDescripcion, setEstadoDescripcion] = useState<string>('');
  const showingIdRef = useRef<number | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    if (!socket) return;
    const handler = (n: any) => {
      if (n.event !== "MEETING_FOLLOWUP_PROMPT") return;
      // Mostrar solo a staff (no clientes)
      if (user?.roles?.includes("cliente")) return;
      // Evitar re-abrir el mismo aviso repetidamente
      const nid = n.id ?? (n.payload && n.payload.notifId) ?? null;
      if (nid && showingIdRef.current === nid) return;
      if (nid) showingIdRef.current = nid;
      setPayload({ ...(n.payload ?? {}), notifId: nid ?? n.payload?.notifId ?? null });
      setOption("1w");
      setCustomDate("");
      setReason("");
      setOpen(true);
      setEstadoSeleccionado(n.payload?.estado ?? null);
      setEstadoDescripcion('');
    };
    socket.on("new-notification", handler);
    return () => {
      socket.off("new-notification", handler);
    };
  }, [socket, user]);

  // On mount, fetch unread IN_APP notifications to catch missed prompts
  useEffect(() => {
    let mounted = true;
    const fetchUnread = async () => {
      if (!mounted) return;
      if (!user || user?.roles?.includes('cliente')) return;
      try {
        const res = await api.get('/notifications?unread=true&channel=IN_APP&take=50');
        const data = res?.data;
        const list = Array.isArray(data) ? data : (data?.rows ?? []);
        if (!list || !list.length) return;
        const promptNotif = list.find((n: any) => n.event === 'MEETING_FOLLOWUP_PROMPT' && n.payload?.reunionId);
        if (!promptNotif) return;
        // Evitar re-abrir el mismo aviso
        if (promptNotif.id && showingIdRef.current === promptNotif.id) return;
        if (promptNotif.id) showingIdRef.current = promptNotif.id;
        setPayload({ ...(promptNotif.payload ?? {}), notifId: promptNotif.id ?? promptNotif.payload?.notifId ?? null });
        setOption("1w");
        setCustomDate("");
        setReason("");
        setOpen(true);
        setEstadoSeleccionado(promptNotif.payload?.estado ?? null);
        setEstadoDescripcion('');
      } catch (err) {
        // ignore fetch errors, keep listening to socket
      }
    };

    // Ejecutar inmediatamente y luego poll cada 15s para asegurar que siempre abra
    fetchUnread();
    pollRef.current = window.setInterval(fetchUnread, 15_000);

    return () => {
      mounted = false;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  // run once on mount; include user to ensure roles available
  }, [user]);

  if (!payload) return null;

  const handleClose = () => {
    setOpen(false);
    setPayload(null);
  };

  const submit = async () => {
    if (!payload?.reunionId) {
      toast.error(t("followupModal.errorNoId", "ID de reunión no disponible"));
      return;
    }
    if (option === "none" && (!reason || reason.trim() === "")) {
      toast.error(t("followupModal.reasonRequired", "Debes indicar un motivo si no vas a agendar más"));
      return;
    }
    let body: any = { option };
    if (option === "custom") {
      if (!customDate) {
        toast.error(t("followupModal.customDateRequired", "Selecciona una fecha personalizada"));
        return;
      }
      const iso = new Date(customDate).toISOString();
      body.customDate = iso;
    }
    if (option === "none") {
      body.reasonIfNone = reason;
    }

    setSubmitting(true);
    try {
      // If user selected a different estado, update it first
      if (estadoSeleccionado && estadoSeleccionado !== payload?.estado) {
        try {
          await api.patch(`/reuniones/${payload.reunionId}/estado`, {
            estado: estadoSeleccionado,
            descripcion: estadoDescripcion || undefined,
          });
        } catch (err) {
          console.error('Error updating estado before followup:', err);
          toast.error(t("followupModal.errorUpdateEstado", "Error al actualizar el estado de la reunión"));
          setSubmitting(false);
          return;
        }
      }

      const { data } = await api.post(`/reuniones/followup/${payload.reunionId}`, body);
      if (data && (data as any).ok) {
      toast.success(t("followupModal.successDeclined", "Decisión registrada correctamente"));
      } else if (data && (data as any).id) {
        toast.success(t("followupModal.successScheduled", "Siguiente reunión agendada correctamente"));
      } else {
        toast.success(t("followupModal.successScheduled", "Acción completada"));
      }
      if (payload?.notifId) {
        try {
          await api.patch(`/notifications/${payload.notifId}/read`);
        } catch (err) {
          // best-effort: no bloquear el flujo si falla el marcado como leído
          console.error('Error marking followup notification as read:', err);
        }
      }
      handleClose();
    } catch (err: any) {
      console.error("Error followup:", err);
      toast.error(err?.response?.data?.message ?? t("followupModal.errorGeneric", "Error al procesar la acción"));
    } finally {
      setSubmitting(false);
    }
  };

  const fechaInicioFmt = payload?.fechaInicio
    ? dayjs(payload.fechaInicio).format("D/M/YYYY • HH:mm")
    : "";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.6)" } },
      }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "visible",
          mr: { xs: 0, md: "280px" },
        },
      }}
    >
      {/* Gorila lateral derecho */}
      <Box
        component="img"
        src={gorilaReunionImg}
        alt="Gorila"
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          transform: "translateX(100%)",
          width: { xs: "200px", sm: "300px", md: "360px" },
          height: "auto",
          zIndex: 1,
          display: { xs: "none", sm: "block" },
        }}
      />
      <DialogTitle sx={{ backgroundColor: "#000", color: "#fff", fontWeight: "bold" }}>
        {t("followupModal.title", "Reunión finalizada")}
      </DialogTitle>
      <DialogContent sx={{ textAlign: "center", pt: 3 }}>
        <Typography variant="body1" sx={{ margin: 1 }}>
          {t("followupModal.subtitle", "¡Que el ritmo no pare! ¿Agendamos la siguiente reunión de seguimiento?")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {payload.alumnoNombre ? `${payload.alumnoNombre} • ${fechaInicioFmt}` : fechaInicioFmt}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap", mb: 2 }}>
          <Button variant={option === "1w" ? "contained" : "outlined"} onClick={() => setOption("1w")}>
            {t("followupModal.option.1w", "1 semana")}
          </Button>
          <Button variant={option === "2w" ? "contained" : "outlined"} onClick={() => setOption("2w")}>
            {t("followupModal.option.2w", "2 semanas")}
          </Button>
          <Button variant={option === "3w" ? "contained" : "outlined"} onClick={() => setOption("3w")}>
            {t("followupModal.option.3w", "3 semanas")}
          </Button>
          <Button variant={option === "1m" ? "contained" : "outlined"} onClick={() => setOption("1m")}>
            {t("followupModal.option.1m", "1 mes")}
          </Button>
          <Button variant={option === "custom" ? "contained" : "outlined"} onClick={() => setOption("custom")}>
            {t("followupModal.option.custom", "Personalizada")}
          </Button>
          <Button variant={option === "none" ? "contained" : "outlined"} color="error" onClick={() => setOption("none")}>
            {t("followupModal.option.none", "No agendar más")}
          </Button>
        </Box>

        {/* Estado de la reunión (opcional) */}
        <Box sx={{ mt: 2, mb: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="estado-select-label">{t("followupModal.estadoLabel", "Estado")}</InputLabel>
            <Select
              labelId="estado-select-label"
              value={estadoSeleccionado ?? ''}
              label={t("followupModal.estadoLabel", "Estado")}
              onChange={(e) => setEstadoSeleccionado(e.target.value as string)}
            >
              <MenuItem value="realizada">{t("followupModal.estado.realizada", "Realizada")}</MenuItem>
              <MenuItem value="cancelada_familia">{t("followupModal.estado.cancelada_familia", "Cancelada (familia)")}</MenuItem>
              <MenuItem value="cancelada_staff">{t("followupModal.estado.cancelada_staff", "Cancelada (staff)")}</MenuItem>
              <MenuItem value="cancelada_sistema">{t("followupModal.estado.cancelada_sistema", "Cancelada (sistema)")}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            placeholder={String(t("followupModal.estadoDescripcionPlaceholder", "Descripción / motivo (opcional)"))}
            value={estadoDescripcion}
            onChange={(e) => setEstadoDescripcion(e.target.value)}
            sx={{ mt: 1 }}
          />
        </Box>

        {option === "custom" && (
          <TextField
            type="datetime-local"
            fullWidth
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            placeholder={String(t("followupModal.customDatePlaceholder", "Selecciona fecha y hora"))}
          />
        )}

        {option === "none" && (
          <TextField
            label={t("followupModal.reasonPlaceholder", "Motivo (obligatorio)")}
            multiline
            minRows={3}
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        <Button
          variant="contained"
          fullWidth
          disabled={submitting}
          onClick={submit}
          sx={{ backgroundColor: "#1a8578", "&:hover": { backgroundColor: "#1a8578" } }}
        >
          {t("followupModal.confirm", "Confirmar")}
        </Button>
        <Button fullWidth onClick={handleClose} disabled={submitting}>
          {t("followupModal.close", "Cerrar")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

